// The point socks — two streamers flying off the picked point.
//
// WHAT IT IS, LITERALLY. The point is a dot. Two flags stream off that dot, one
// for wind and one for the sea, each lying along its own bearing. How LONG a flag
// flies and what COLOUR it is are the same fact told twice: how hard it is
// blowing, how big the sea is, against the limit for the operation you have
// armed. Full length is at your limit. That is the whole instrument.
//
// It is drawn ON the water at the point rather than in a panel beside it, because
// the question it answers — "what is it doing HERE, and can we work" — is a
// question about a place, and an instrument about a place should be at the place.
//
// WHY A FLAG AND NOT AN ARROW. The console has exactly ONE arrow, for every
// quantity, and that was hard won: see chart/marks.ts ARROW_GLYPH and
// docs/GLYPH_FINDINGS.md, the record of three invented shapes that were each
// defensible in every number and wrong on the screen. A flag is not a second
// arrow — it is a different KIND of mark. An arrow says only which way; a flag
// says which way AND how hard, because a flag that is not being pushed does not
// fly. Nobody has to be taught that, which is the point.
//
// The flags stream the way the wind and sea are GOING, which is what a real flag
// does and what every direction mark in this console does. The bearings are
// quoted the other way round, FROM, because that is how they are quoted at sea
// and how the models publish them (measured, not assumed — docs/GLYPH_FINDINGS).
//
// TELLING THEM APART. Length and colour are both spent on intensity, so neither
// is available to say which flag is which — the exact trap the glyph findings
// describe. So the label does it, the way the meteogram's panels and the
// conditions table's rows do: each flag carries its own reading at the tip, in
// words, and that tag is also the thing you click for an explanation.
//
// NO STYLESHEET. All of it is canvas read from the palette at draw time, so it
// re-casts across the Day/Dusk/Night bridge tables for free.

import { compass16 } from "../data/stormlist.js";
import { cssVar } from "../chart/marks.js";
import { firstWithData, type PointForecast } from "../forecast/openmeteo.js";
import type { EffectiveLimit } from "../ops/registry.js";
import { attachHelp } from "../help/popover.js";
import { getCursor, indexAtTime, subscribeCursor } from "../time/cursor.js";
import { convLen, fmtLimitLen, lenUnit, subscribeUnits } from "../ui/units.js";
import { waveLimitFractionAt, windLimitFractionAt, workGrade } from "../ops/workability.js";
import { CROSS_SEA, OPPOSED_WIND } from "../ops/briefing.js";

// --- geometry ------------------------------------------------------------------

/** The canvas is square and centred on the point, so a flag can fly to any
 *  bearing without the box having to be reasoned about per direction. */
const BOX = 240;
const MID = BOX / 2;
/** Flag length at full scale (at the limit), and the shortest a flag with any
 *  reading at all is allowed to be. A calm still has a direction, and a flag
 *  shrunk to nothing would report "no data" when it means "almost nothing". */
const FLY_MAX = 74;
const FLY_MIN = 13;
/** Half-width where the sleeve leaves the dot, and at its open mouth. It flares
 *  outward, the way a windsock does — narrow at the mast, wide at the fly. The
 *  flare is what makes a short sleeve still read as a sleeve. */
const HOIST_R = 1.6;
const FLY_R = 7;
/** How far past full length a breach is allowed to stream, so "over the limit"
 *  does not render identically to "exactly on it". */
const OVERRUN = 9;

/** What one flag has been asked to draw for the hour under the playhead. */
interface FlagSpec {
  /** "WIND" / "SEA" — the quantity, since one shape serves both. */
  name: string;
  /** The reading in the operator's units, for the tag ("24 kn", "2.4 m"). */
  valueText: string;
  /** Bearing in the quantity's own convention, or null when there is none. */
  dirDeg: number | null;
  /** Quoted TOWARD rather than FROM. False for both today; present so a current
   *  flag cannot be added without answering the question. */
  toward: boolean;
  /** Reading ÷ full scale. May exceed 1 — that is a breach, and it shows. */
  frac: number | null;
  /** 0..1 on the WORK_GRADES scale, or null when nothing grades it. */
  p: number | null;
  /** "limit 25 kn" / "scale 35 kn" — what full length means, for the tag title. */
  scaleNote: string;
}

/**
 * One flag: a sleeve flaring from the dot outward along `heading`, narrow where
 * it leaves the point and open at the fly.
 *
 * It flares AWAY from the dot on purpose. A pennant tapering to a point was
 * tried first and it was worse for the same reason the invented glyphs were
 * worse — a taper gives the shape a POINT, and a point is a direction claim of
 * its own, sitting a few pixels from an arrow field already making direction
 * claims. Flaring outward from an anchored hoist is a windsock and reads as one;
 * the wide end is the mouth, not a head.
 */
function flagPath(g: CanvasRenderingContext2D, heading: number, fly: number, grow = 0): void {
  const a = (heading * Math.PI) / 180;
  const dx = Math.sin(a);
  const dy = -Math.cos(a);
  const nx = -dy;
  const ny = dx;
  // The hoist is AT the point — zero, not a few pixels clear of it.
  //
  // It used to start 5 px out "to clear the dot", and that was a real defect on
  // the map rather than a detail: every sleeve began 5 px along ITS OWN heading,
  // so the vertex the eye reads as the anchor sat 5 px away from the dot in
  // whatever direction the flags happened to lie — visibly off when both pointed
  // the same way, and not a single vertex at all when they diverged. The site
  // dot is 11 px and opaque, so it covers the narrow hoists anyway, and a sleeve
  // emerging from under the dot is the correct reading: it is anchored there.
  const s0 = 0;
  const r0 = HOIST_R + grow;
  // The mouth opens WITH the sleeve. At a fixed 7 px a barely-flying sleeve is
  // wider than it is long — a calm rendered as a fat little fan, which reads as
  // something rather than as almost nothing. Floored so a short sleeve is still
  // a sleeve and not a hair.
  const r1 = Math.max(3.4, FLY_R * Math.min(1, fly / 46)) + grow;
  const p = (s: number, r: number, side: number): [number, number] => [
    MID + dx * s + nx * r * side,
    MID + dy * s + ny * r * side,
  ];
  const a1 = p(s0, r0, 1);
  const b1 = p(fly, r1, 1);
  const b2 = p(fly, r1, -1);
  const a2 = p(s0, r0, -1);
  g.beginPath();
  g.moveTo(a1[0], a1[1]);
  g.lineTo(b1[0], b1[1]);
  // A rounded fly end — a flat cut reads as a bar that was truncated, a curve
  // reads as the open mouth of a sleeve, which is what it is.
  g.quadraticCurveTo(
    MID + dx * (fly + r1 * 0.9),
    MID + dy * (fly + r1 * 0.9),
    b2[0],
    b2[1],
  );
  g.lineTo(a2[0], a2[1]);
  g.closePath();
}

/** Where a tag sits: `out` beyond the dot along the heading. */
function tipAt(heading: number, out: number): [number, number] {
  const a = (heading * Math.PI) / 180;
  return [MID + Math.sin(a) * out, MID - Math.cos(a) * out];
}

/** Clear of the fabric at the fly. */
const TAG_GAP = FLY_R + 6;
/** Centre-to-centre a pill needs to stop overlapping its neighbour. */
const TAG_CLEAR = 26;

/**
 * How far out each tag sits, given both flags.
 *
 * In a calm, or in any hour where wind and sea are both well inside the limit,
 * the two flags are short AND lie close together — which is exactly when the two
 * pills land on top of each other and the instrument becomes unreadable in the
 * one condition it should be easiest to read. Both are pushed outward along
 * their own bearings until they clear; the flags themselves never move, so the
 * reading is untouched and only the labelling gives ground.
 */
function tagRadii(
  aDeg: number,
  aFly: number,
  bDeg: number,
  bFly: number,
): [number, number] {
  let ra = aFly + TAG_GAP;
  let rb = bFly + TAG_GAP;
  for (let n = 0; n < 14; n++) {
    const [ax, ay] = tipAt(aDeg, ra);
    const [bx, by] = tipAt(bDeg, rb);
    if (Math.hypot(ax - bx, ay - by) >= TAG_CLEAR) break;
    // Push the shorter flag's tag, so the longer one keeps the position that
    // reads as "this is the tip of that bar".
    if (ra <= rb) ra += 5;
    else rb += 5;
  }
  return [ra, rb];
}

/**
 * How far apart the two flags lie, and what that is called.
 *
 * A NOTE ON THE NAME, because it matters. In this console "crossed sea" already
 * means one specific thing — SWELL crossed with WIND SEA, tested by
 * briefing.ts's crossSea() against the model's own partition, and printed as the
 * x / X marks in the conditions table. That is a property of the sea alone and
 * is invisible in these two flags, which show wind and the COMBINED sea.
 *
 * What the flags can diverge about is wind against sea, which is a different
 * hazard: wind opposing a running sea steepens the crests and breaks them early,
 * so the sea works out rougher than its height and period imply. Calling that
 * "crossed" would put two meanings on one phrase and let the map say a word the
 * table is using for something else.
 *
 * The THRESHOLDS are the console's existing ones, not new numbers: 60° is
 * CROSS_SEA.sepDeg and 120° is OPPOSED_WIND.sepDeg, the same pair the briefing
 * prose and the table's marks are cut at. Opposed additionally needs
 * OPPOSED_WIND.minWindKn of wind, because a 6 kn breeze against a swell does
 * nothing to it — which is exactly the check windAgainstSwell makes.
 */
function divergence(
  windDeg: number,
  seaDeg: number,
  windKn: number | null,
): { sep: number; label: string; grade: 1 | 2 } | null {
  const raw = Math.abs(((windDeg - seaDeg) % 360) + 360) % 360;
  const sep = raw > 180 ? 360 - raw : raw;
  if (sep >= OPPOSED_WIND.sepDeg && windKn !== null && windKn >= OPPOSED_WIND.minWindKn) {
    return { sep, label: "WIND AGAINST SEA", grade: 2 };
  }
  if (sep >= CROSS_SEA.sepDeg) return { sep, label: "WIND ACROSS SEA", grade: 1 };
  return null;
}

export interface SockInput {
  fc: PointForecast;
  limit: EffectiveLimit | null;
}

export interface PointSocks {
  /** Hand to SeaMap.attachAtPoint. */
  root: HTMLElement;
  /** Point the flags at a forecast, or null to hide them. */
  update(input: SockInput | null): void;
  /** Swap only the limit — arming a different vessel or operation changes what
   *  full length means, and therefore the colour, without the weather having
   *  moved. Same split as the conditions table's refreshSelection. */
  setLimit(limit: EffectiveLimit | null): void;
  destroy(): void;
}

/** The sentence a screen reader gets, and the tag's own tooltip. */
function describe(s: FlagSpec): string {
  const dir =
    s.dirDeg === null
      ? "direction not available"
      : `${s.toward ? "setting toward" : "from"} ${compass16(s.dirDeg)}, ${Math.round(s.dirDeg)}°`;
  const verdict = s.p === null ? "no operation armed, so no verdict" : workGrade(s.p).label;
  return `${s.name} ${s.valueText}, ${dir} · ${verdict} against ${s.scaleNote}`;
}

export function createPointSocks(): PointSocks {
  // pointer-events:none throughout except the two tags. The flags lie over the
  // water, and water is the thing the operator clicks to move the point — an
  // instrument about the point must not eat the gesture that moves it.
  const root = document.createElement("div");
  // position:ABSOLUTE, deliberately, and it is load-bearing. MapLibre's own
  // .maplibregl-marker rule is position:absolute, and an inline position:relative
  // here overrides it — which drops the element back into normal flow inside the
  // marker container, where it picks up a static offset equal to the height of
  // whatever marker precedes it before MapLibre's transform is even applied. That
  // is precisely the bug where the flags hung 14 px below the point: 14 px is the
  // site dot's height. Absolute keeps MapLibre's positioning intact AND still
  // gives the tags a containing block, which is the only reason relative was
  // reached for.
  root.style.cssText =
    `position:absolute;top:0;left:0;width:${BOX}px;height:${BOX}px;` +
    "pointer-events:none;user-select:none;";
  root.hidden = true;

  const cv = document.createElement("canvas");
  cv.style.cssText = `position:absolute;inset:0;width:${BOX}px;height:${BOX}px;`;
  root.appendChild(cv);

  /** A tag: the reading in words at the flag's tip, and the click target for the
   *  explanation. A button, so it is reachable from a keyboard — a chart you can
   *  only reach with a mouse is a chart half the bridge cannot reach at all. */
  function makeTag(): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    // A dark pill, not a coloured one. The colour is spent on the bar; a tag in
    // the same hue would say the same thing twice and lose the reading to it.
    b.style.cssText =
      "position:absolute;transform:translate(-50%,-50%);white-space:nowrap;" +
      "pointer-events:auto;cursor:help;border:0;border-radius:9px;padding:2px 6px;" +
      "font:600 10.5px/1.2 var(--mono);letter-spacing:-0.01em;";
    attachHelp(b, "map.socks");
    return b;
  }
  const windTag = makeTag();
  const seaTag = makeTag();
  // The divergence mark. Same pill, so it is read as part of the same
  // instrument, and it opens the same explanation.
  const crossTag = makeTag();
  crossTag.hidden = true;
  root.append(windTag, seaTag, crossTag);

  let held: SockInput | null = null;

  /** Heading and length, or null when there is nothing to fly. */
  function layout(s: FlagSpec): { heading: number; fly: number } | null {
    if (s.dirDeg === null || s.frac === null) return null;
    return {
      heading: s.toward ? s.dirDeg : s.dirDeg + 180, // fly the way it is going
      fly:
        s.frac > 1
          ? FLY_MAX + OVERRUN
          : FLY_MIN + Math.max(0, Math.min(1, s.frac)) * (FLY_MAX - FLY_MIN),
    };
  }

  function drawFlag(
    g: CanvasRenderingContext2D,
    s: FlagSpec,
    tag: HTMLButtonElement,
    lay: { heading: number; fly: number } | null,
    tagOut: number,
  ): void {
    const ink = cssVar("--ink", "#16232e");
    const grade = s.p === null ? null : workGrade(s.p);
    const colour = grade === null ? ink : cssVar(grade.varName, grade.fallback);

    if (lay === null) {
      tag.hidden = true;
      return;
    }
    const { heading, fly } = lay;

    g.save();
    // A paper-coloured casing under the bar. The map beneath is a live colour
    // ramp — a green bar laid straight onto the green of a 15 kn field is a bar
    // that is not there. This is the same argument the plates under the map's
    // other labels make.
    flagPath(g, heading, fly, 1.6);
    g.fillStyle = cssVar("--surface", "#ffffff");
    g.globalAlpha = 0.85;
    g.fill();

    flagPath(g, heading, fly);
    g.fillStyle = colour;
    g.globalAlpha = grade === null ? 0.6 : 1;
    g.fill();
    g.restore();

    const [tx, ty] = tipAt(heading, tagOut);
    tag.hidden = false;
    tag.style.left = `${tx}px`;
    tag.style.top = `${ty}px`;
    tag.style.color = cssVar("--paper", "#f3f4f2");
    tag.style.background = cssVar("--ink", "#16232e");
    tag.textContent = `${s.name} ${s.valueText}`;
    tag.title = describe(s);
    tag.setAttribute("aria-label", describe(s));
  }

  function render(): void {
    if (held === null) {
      root.hidden = true;
      return;
    }
    const { fc, limit } = held;
    // The playhead when there is one, the current hour when there is not. The
    // cursor is null until something is hovered or scrubbed, and the track meters
    // answer that with "—" — right for a row of a chart, wrong for an instrument
    // standing on the map, which would simply be absent on a freshly loaded
    // console until the operator happened to touch the timeline.
    const i = indexAtTime(fc.times, getCursor()) ?? fc.nowIndex;
    if (i < 0 || i >= fc.times.length) {
      root.hidden = true;
      return;
    }

    // The same pick the conditions table makes — first model with data — so the
    // flag and the cell for the same hour can never print two different numbers.
    const windKn = (fc.wind ? firstWithData(fc.wind.models) : null)?.data[i] ?? null;
    const waveM = (fc.waves ? firstWithData(fc.waves.height) : null)?.data[i] ?? null;
    if (windKn === null && waveM === null) {
      root.hidden = true;
      return;
    }

    const windFull = limit?.wind_kn ?? NO_LIMIT_WIND_KN;
    const waveFull = limit?.wave_m ?? NO_LIMIT_WAVE_M;

    const dpr = Math.min(window.devicePixelRatio > 0 ? window.devicePixelRatio : 1, 2);
    cv.width = Math.round(BOX * dpr);
    cv.height = Math.round(BOX * dpr);
    const g = cv.getContext("2d");
    if (g === null) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, BOX, BOX);

    const windSpec: FlagSpec = {
        name: "WIND",
        valueText: windKn === null ? "—" : `${Math.round(windKn)} kn`,
        dirDeg: fc.wind?.directionDeg[i] ?? null,
        toward: false,
        frac: windKn === null ? null : windKn / windFull,
        // The ensemble fraction, not the ratio — the same function the table's
        // wind row and the workability ribbon read, so a red flag beside a red
        // cell is arithmetic rather than coincidence.
        p: limit === null || windKn === null ? null : windLimitFractionAt(fc, i, limit.wind_kn),
        scaleNote:
          limit === null ? `a ${NO_LIMIT_WIND_KN} kn scale` : `a ${limit.wind_kn} kn limit`,
    };
    const seaSpec: FlagSpec = {
        name: "SEA",
        valueText: waveM === null ? "—" : `${convLen(waveM).toFixed(1)} ${lenUnit()}`,
        dirDeg: fc.waves?.directionDeg[i] ?? null,
        toward: false,
        frac: waveM === null ? null : waveM / waveFull,
        p: limit === null || waveM === null ? null : waveLimitFractionAt(fc, i, limit.wave_m),
        scaleNote:
          limit === null
            ? `a ${fmtLimitLen(NO_LIMIT_WAVE_M)} scale`
            : `a ${fmtLimitLen(limit.wave_m)} limit`,
    };

    const windLay = layout(windSpec);
    const seaLay = layout(seaSpec);
    // Tag placement is decided for the PAIR, not per flag: in light conditions
    // both are short and close together, which is exactly when two pills land on
    // top of each other.
    const [windOut, seaOut] =
      windLay !== null && seaLay !== null
        ? tagRadii(windLay.heading, windLay.fly, seaLay.heading, seaLay.fly)
        : [(windLay?.fly ?? 0) + TAG_GAP, (seaLay?.fly ?? 0) + TAG_GAP];
    drawFlag(g, windSpec, windTag, windLay, windOut);
    drawFlag(g, seaSpec, seaTag, seaLay, seaOut);

    const div =
      windLay !== null && seaLay !== null
        ? divergence(windLay.heading, seaLay.heading, windKn)
        : null;
    if (div === null) {
      crossTag.hidden = true;
    } else {
      // An arc struck between the two flags, inside both of them, so the mark is
      // read as the angle BETWEEN the pair rather than as a third quantity.
      const r = Math.max(20, Math.min(windLay!.fly, seaLay!.fly) * 0.62);
      const toRad = (d: number): number => ((d - 90) * Math.PI) / 180;
      const a1 = toRad(windLay!.heading);
      const a2 = toRad(seaLay!.heading);
      // Sweep the SHORT way round, which is the angle the eye is measuring.
      let delta = a2 - a1;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      const tint = cssVar(div.grade === 2 ? "--serious" : "--warn", "#b9791a");

      g.save();
      g.lineCap = "butt";
      // Cased like the flags: the arc has to survive the field beneath it.
      g.strokeStyle = cssVar("--surface", "#ffffff");
      g.lineWidth = 4.5;
      g.globalAlpha = 0.8;
      g.beginPath();
      g.arc(MID, MID, r, a1, a1 + delta, delta < 0);
      g.stroke();

      g.globalAlpha = 1;
      g.strokeStyle = tint;
      g.lineWidth = 2;
      g.setLineDash([4, 3]);
      g.beginPath();
      g.arc(MID, MID, r, a1, a1 + delta, delta < 0);
      g.stroke();
      g.restore();

      const mid = a1 + delta / 2;
      crossTag.hidden = false;
      crossTag.style.left = `${MID + Math.cos(mid) * (r + 13)}px`;
      crossTag.style.top = `${MID + Math.sin(mid) * (r + 13)}px`;
      crossTag.style.background = tint;
      crossTag.style.color = cssVar("--paper", "#f3f4f2");
      crossTag.textContent = `${div.label} ${Math.round(div.sep)}°`;
      const why =
        div.grade === 2
          ? "Wind opposing the running sea — crests steepen and break early, so the sea works rougher than its height and period imply."
          : "Wind and sea running across one another — an uneven, awkward surface for station-keeping and for anything over the side.";
      crossTag.title = `${div.label}, ${Math.round(div.sep)}° apart. ${why}`;
      crossTag.setAttribute("aria-label", crossTag.title);
    }

    // No dot is drawn here: the site marker IS the dot (map.ts siteDot), and it
    // carries a z-index above this canvas so the point is never underneath the
    // flags that belong to it. Two dots at one coordinate is one dot too many.

    root.hidden = false;
  }

  const unsubs: Array<() => void> = [subscribeCursor(render), subscribeUnits(render)];
  // The three bridge tables swap by data-theme, and every colour above is read
  // from the palette at draw time — so a theme change is a repaint, not a rebuild.
  const themeObs = new MutationObserver(render);
  themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  return {
    root,
    update(input: SockInput | null): void {
      held = input;
      render();
    },
    setLimit(limit: EffectiveLimit | null): void {
      if (held === null) return;
      held = { fc: held.fc, limit };
      render();
    },
    destroy(): void {
      for (const u of unsubs) u();
      themeObs.disconnect();
      root.remove();
    },
  };
}

/** Where the scale sits when no operation has been armed. Deliberately generous —
 *  a 35 kn blow and a 4 m sea are near the top of what this fleet works in, so an
 *  unscaled flag reads as "a lot" only when it is. */
const NO_LIMIT_WIND_KN = 35;
const NO_LIMIT_WAVE_M = 4;
